/**
 * Die Schallsignale, im Browser gerechnet.
 *
 * Die App liefert keine Audiodatei aus. Jedes Signal entsteht als Folge von Abtastwerten
 * aus der Zeitleiste, die `core/signals` aus den Daten baut, und wird erst danach an die
 * Web-Audio-Schnittstelle gereicht. Das hat zwei Gründe: der Download bleibt klein, und
 * der erzeugte Ton ist prüfbar. Ein Test kann die Abtastwerte messen und nachrechnen,
 * ob wirklich drei Töne von je einer Sekunde herauskommen und ob die Tonhöhe im Band
 * aus Anhang III liegt.
 */
import type { SignalData } from '@/core/schema'
import { timeline, type SignalStep } from '@/core/signals'
import { whistle, type Whistle } from '@/core/whistle'

export const SAMPLE_RATE = 22050

/**
 * Obertöne einer Schiffspfeife. Der Grundton trägt am meisten Energie, darum misst ein
 * Test ihn als Tonhöhe; die Obertöne machen den Klang blechern statt sinusartig.
 */
const HARMONICS: [number, number][] = [
  [1, 1],
  [2, 0.55],
  [3, 0.3],
  [4, 0.16],
  [5, 0.08],
]

/** Anstieg und Abfall eines Tons in Sekunden, damit es nicht knackt. */
const RAMP_S = 0.05

/** Glocke und Gong: Teiltöne im festen Verhältnis, jeder mit eigener Abklingzeit. */
const BELL_PARTIALS: [number, number, number][] = [
  [1, 1, 1.4],
  [2.76, 0.6, 0.9],
  [5.4, 0.35, 0.6],
  [8.9, 0.2, 0.4],
]
const BELL_HZ = 700
const GONG_HZ = 190
/** Regel 35(g): schnelles Läuten, also ein Schlag alle 0,22 Sekunden. */
const STRIKE_INTERVAL_S = 0.22

export interface RenderOptions {
  sampleRate?: number
  voice?: Whistle
}

/** Länge des Signals in Abtastwerten. */
export function sampleCount(steps: SignalStep[], sampleRate: number): number {
  return steps.reduce((n, step) => n + Math.round(step.duration * sampleRate), 0)
}

/**
 * Ein Signal als Abtastwerte zwischen -1 und 1. Der Puffer liegt in einem gewöhnlichen
 * ArrayBuffer, weil die Web-Audio-Schnittstelle keinen geteilten Speicher annimmt.
 */
export function renderSignal(signal: SignalData, options: RenderOptions = {}): Float32Array<ArrayBuffer> {
  return renderSteps(timeline(signal), options)
}

/**
 * Eine fertige Zeitleiste als Abtastwerte.
 *
 * Getrennt von `renderSignal`, weil zwei Ordnungen denselben Klang nutzen, aber
 * verschiedene Längen haben: auf See dauert ein langer Ton fünf Sekunden, auf dem Rhein
 * vier. Jede rechnet ihre Zeitleiste selbst (`signals.ts`, `binnenSignale.ts`), und
 * dieser Teil macht daraus nur noch Schall.
 */
export function renderSteps(steps: SignalStep[], options: RenderOptions = {}): Float32Array<ArrayBuffer> {
  const sampleRate = options.sampleRate ?? SAMPLE_RATE
  const voice = options.voice ?? whistle('mittel')
  const out = new Float32Array(sampleCount(steps, sampleRate))
  let at = 0
  for (const step of steps) {
    const length = Math.round(step.duration * sampleRate)
    switch (step.kind) {
      case 'short':
      case 'prolonged':
      case 'continuous':
        tone(out, at, length, voice.hz, sampleRate)
        break
      case 'bell':
        strikes(out, at, length, BELL_HZ, sampleRate)
        break
      case 'gong':
        strikes(out, at, length, GONG_HZ, sampleRate)
        break
      case 'silence':
        break
    }
    at += length
  }
  return out
}

/** Ein Pfeifenton mit Obertönen und weichen Flanken. */
function tone(out: Float32Array, offset: number, length: number, hz: number, sampleRate: number): void {
  const ramp = Math.min(Math.round(RAMP_S * sampleRate), Math.floor(length / 4))
  const weight = HARMONICS.reduce((n, [, amp]) => n + amp, 0)
  for (let i = 0; i < length; i += 1) {
    const t = i / sampleRate
    let value = 0
    for (const [factor, amp] of HARMONICS) value += amp * Math.sin(2 * Math.PI * hz * factor * t)
    out[offset + i] = (value / weight) * 0.8 * edge(i, length, ramp)
  }
}

/** Lautstärke an den Rändern eines Tons: linearer Anstieg, linearer Abfall. */
function edge(i: number, length: number, ramp: number): number {
  if (ramp === 0) return 1
  if (i < ramp) return i / ramp
  if (i > length - ramp) return Math.max(0, (length - i) / ramp)
  return 1
}

/**
 * Glocke oder Gong. Ein einzelner Schlag darf lange ausklingen; beim schnellen Läuten
 * nach Regel 35(g) folgt alle 0,22 Sekunden der nächste, und dann muss der vorige weit
 * genug abgeklungen sein, dass man die Schläge noch als Schläge hört.
 */
function strikes(out: Float32Array, offset: number, length: number, hz: number, sampleRate: number): void {
  const step = Math.round(STRIKE_INTERVAL_S * sampleRate)
  const single = length <= Math.round(0.6 * sampleRate)
  if (single) {
    strike(out, offset, length, hz, sampleRate, 1, 0.8)
    return
  }
  for (let start = 0; start < length; start += step) {
    strike(out, offset + start, length - start, hz, sampleRate, 0.25, 0.7)
  }
}

function strike(
  out: Float32Array,
  offset: number,
  length: number,
  hz: number,
  sampleRate: number,
  decayScale: number,
  gain: number,
): void {
  const weight = BELL_PARTIALS.reduce((n, [, amp]) => n + amp, 0)
  for (let i = 0; i < length; i += 1) {
    const t = i / sampleRate
    let value = 0
    for (const [factor, amp, decay] of BELL_PARTIALS) {
      value += amp * Math.exp(-t / (decay * decayScale)) * Math.sin(2 * Math.PI * hz * factor * t)
    }
    out[offset + i] = clamp(out[offset + i]! + (value / weight) * gain)
  }
}

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v))
}
